import * as React from "react";
import type {IToolConfig, SpaceTool} from "../../logic3d/features/tools/Tools";
import {ReactUtils} from "../../../../../utils/ReactUtils";
import {FocusLoss} from "../../../../../../utils/ui/focus/FocusLoss";
import {SVGIcon} from "../../../../../widgets/button/SVGIcon";
import {HTMLUtils} from "../../../../../../utils/HTML/HTMLUtils";
import type {Color} from "../../../../../../generated/api/base";
import type {IToolButtonProps} from "./ToolButton";

interface ISharedToolGroupProps {
	readonly tools: {
		[id in SpaceTool]: IToolConfig;
	};
	readonly chosenTool: SpaceTool; // the subtool that is currently chosen within the toolgroup
	readonly children: React.ReactElement<IToolButtonProps>[];
	readonly onClick: (id: SpaceTool) => void;
	readonly color?: Color;
}

interface ISharedToolGroupState {
	isOpen: boolean;
}

/**
 * Represents a ToolGroup, but it's chosen tool is a property, so it can be synced across the instances
 */

export class SharedToolGroup extends React.Component<ISharedToolGroupProps, ISharedToolGroupState> {
	private _element = React.createRef<HTMLDivElement>();
	private _longPressThreshold = 750; // ms
	private _timeoutId: number = null;

	constructor(props: ISharedToolGroupProps) {
		super(props);
		this.state = {
			isOpen: false,
		};
	}

	private onClick = (event: React.MouseEvent<HTMLElement>) => {
		const clickedOnSubButton = HTMLUtils.doesAncestorHaveClass(event.target as HTMLElement, "subMenu");

		if (clickedOnSubButton) {
			return;
		} else {
			this.setState({
				isOpen: this.isActive && !this.state.isOpen,
			});

			this.props.onClick(this.props.chosenTool);
		}
	};

	private onLongPress = () => {
		this.props.onClick(this.props.chosenTool);
		this.setState({
			isOpen: true,
		});
	};

	private get isActive(): boolean {
		const children = this.props.children as React.ReactElement<IToolButtonProps>[];

		return children.some((child) => child.props.active);
	}

	private onBlur = (event: MouseEvent | TouchEvent) => {
		this.setState({
			isOpen: false,
		});

		return false;
	};

	private onPointerDown = () => {
		clearTimeout(this._timeoutId);
		this._timeoutId = window.setTimeout(this.onLongPress, this._longPressThreshold);
	};

	private onPointerUp = () => {
		clearTimeout(this._timeoutId);
	};

	public override componentDidMount() {
		FocusLoss.listen(this._element.current, this.onBlur);
	}

	public override componentWillUnmount() {
		FocusLoss.stopListen(this._element.current, this.onBlur);
	}

	public override render() {
		const children = this.props.children as React.ReactElement<IToolButtonProps>[];
		const svgStyle: React.CSSProperties = {};

		const subMenuItems = children.map((container: React.ReactElement<IToolButtonProps>, index: number) => {
			const onClick = () => {
				container.props.onClick();
				this.setState({
					isOpen: false,
				});
			};

			return React.cloneElement(container, {key: index, onClick: onClick});
		});

		const chosenTool = this.props.tools[this.props.chosenTool];

		if (this.props.color) {
			svgStyle.fill = `#${this.props.color.hex}`;
		}

		return (
			<div
				ref={this._element}
				className={ReactUtils.cls("btn", {active: this.isActive, isBordered: this.isActive})}
				title={chosenTool.title}
				onClick={this.onClick}
				onMouseDown={this.onPointerDown}
				onMouseUp={this.onPointerUp}
				onTouchStart={this.onPointerDown}
				onTouchEnd={this.onPointerUp}
				onTouchCancel={this.onPointerUp}
			>
				<SVGIcon
					icon={chosenTool.icon}
					style={svgStyle}
				/>
				<div className="arrowWrapper">
					<i className="arrowDown corner"></i>
				</div>
				<div className={ReactUtils.cls("subMenu", {open: this.state.isOpen})}>{subMenuItems}</div>
			</div>
		);
	}
}
